import { PurchaseError } from 'models/errors/purchase-error';
import { ITopOffersTradePreviewDetails } from 'models/top-offers/i-top-offers';
import { ITradePreviewDetails } from 'models/trade/i-trade-preview-details.interface';

import { CheckoutProgress } from 'enums/checkout-progress.enum';

import { WEB3 } from 'constants/web3.constant';

import { adaptStateHash } from 'services/market.service';

import { IEvents } from 'hooks/events/useEventsParams';

import { parseDecimal } from 'utils/parser.utils';
import { validateTokenAmount } from 'utils/validation.utils';

import { useCurrencyWithBalance } from './useCurrency';
import { usePurchaseCore } from './usePurchaseCore';

export function usePurchase(
  tradePreviewDetails: ITradePreviewDetails | ITopOffersTradePreviewDetails,
  events?: IEvents
) {
  const core = usePurchaseCore();
  const currency = useCurrencyWithBalance(tradePreviewDetails.tradeDetails.currency_id);

  // Calculate proper RMV amount with decimal conversion (only if currency is loaded)
  const amountRmv = currency
    ? parseFloat(parseDecimal(tradePreviewDetails.tradeDetails.price, currency.decimal))
    : undefined;

  const buyParams = {
    nftId: tradePreviewDetails.assetDetails.token_id,
    nftName: tradePreviewDetails.assetDetails.name,
    fnftsAmount: parseInt(tradePreviewDetails.tradeDetails.amount),
    amountRmv,
  };

  const isPurchaseFinished = () => !core.isAddFundsOpen && core.progressStep === CheckoutProgress.end;
  const isCheckoutInProgress = () => !core.isAddFundsOpen && core.progressStep === CheckoutProgress.start;
  const isPurchaseInProgress = () => !core.isAddFundsOpen && core.progressStep === CheckoutProgress.inProgress;

  const purchase = async () => {
    core.setConfirmationPending(true);

    try {
      // Validate network
      await core.validateNetwork(tradePreviewDetails.assetDetails.contract.network);

      // Validate seller address
      core.validateSellerAddress(tradePreviewDetails.tradeDetails.seller.address);

      // Ensure currency is loaded
      if (!currency) {
        throw PurchaseError.unknown(new Error('Currency not loaded'));
      }

      // Ensure signature exists
      if (!tradePreviewDetails.tradeDetails.signature) {
        throw PurchaseError.unknown(new Error('Trade signature not found'));
      }

      // Check sufficient balance
      const hasSufficientBalance = core.checkSufficientBalance(
        currency,
        tradePreviewDetails.tradeDetails.price.toString(),
        buyParams
      );
      if (!hasSufficientBalance) {
        core.setConfirmationPending(false);
        return;
      }

      // Ensure currency permissions
      await core.ensureCurrencyPermissions(currency, tradePreviewDetails.tradeDetails.price.toString());

      // Check if the signature has already been used before attempting purchase
      const isSignatureUsed = await core.checkSignatureHashUsed(tradePreviewDetails.tradeDetails.signature_hash);

      if (isSignatureUsed) {
        throw PurchaseError.signatureUsed();
      }

      // Validate token amount doesn't overflow
      validateTokenAmount(tradePreviewDetails.tradeDetails.amount);

      // Execute purchase
      await core.lgoWeb3.purchesAsset({
        to: tradePreviewDetails.tradeDetails.seller.address,
        signature: tradePreviewDetails.tradeDetails.signature,
        tradeRequest: {
          nonce: tradePreviewDetails.tradeDetails.nonce.toString(),
          expirationTime: tradePreviewDetails.tradeDetails.expiration_time.toString(),
          tradeTokenAddress: currency.address,
          itemsTokenAddress: tradePreviewDetails.assetDetails.contract.address,
          items: [
            {
              tokenId: tradePreviewDetails.assetDetails.token_id,
              amount: +tradePreviewDetails.tradeDetails.amount,
              stateHash: adaptStateHash(tradePreviewDetails.tradeDetails.state_hash),
            },
          ],
          offerValue: tradePreviewDetails.tradeDetails.price.toString(),
          to: WEB3.nullAddress,
        },
        walletAddress: core.accountAddress,
        onTransactionHash: (hash: string) => core.handleTransactionHash(hash, buyParams, events),
        offerAccepted: () => core.handlePurchaseSuccess(buyParams, events),
        offerCanceled: () => core.handlePurchaseCanceled(buyParams, events),
      });
    } catch (error) {
      core.handlePurchaseError(error, buyParams, events);
    }
  };

  return {
    confirmationPending: core.confirmationPending,
    fundsRemaining: core.fundsRemaining,
    fundsRemainingCurrency: core.fundsRemainingCurrency,
    isAddFundsOpen: core.isAddFundsOpen,
    isPurchaseFinished,
    isCheckoutInProgress,
    isPurchaseInProgress,
    progressStep: core.progressStep,
    purchase,
    setIsAddFundsOpen: core.setIsAddFundsOpen,
    transactionHash: core.transactionHash,
  };
}
